import { useState } from 'react';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { Icons, Actions } from '@/theme';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { updateCurrentUser } from '@/auth/authData';
import { useSwitchContext } from '@/features/context/hooks/useContextSwitching';
import { testIds } from '@/test/testIds';
import { CreateNetworkDialog } from '../CreateNetworkDialog';
import { CreateOrganizationDialog } from '../CreateOrganizationDialog';

export interface ContextCreateMenuProps {
  /**
   * Called after a brand-new context has been created AND switched into.
   * The switcher uses this to redirect to /manage (overriding the default-home
   * redirect from useSwitchContext) and to close its host dialog.
   */
  onCreated?: () => void;
}

/**
 * ContextCreateMenu — staff-only "Create" dropdown for spinning up a brand-new
 * organization or network. Lives on the context switcher's search row (inside
 * the Organization/Network dialog). On success it switches into the new context;
 * useSwitchContext routes to the new context's default home (issue #4094), then
 * `onCreated` lets the switcher override that to /manage and close the dialog.
 */
export function ContextCreateMenu({ onCreated }: ContextCreateMenuProps) {
  const { isStaff } = useCurrentUser();
  const switchContext = useSwitchContext();

  const [createNetworkOpen, setCreateNetworkOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  // Bump a session counter on each open so the child form remounts fresh
  // (state re-inits) without an in-child reset effect.
  const [networkSession, setNetworkSession] = useState(0);
  const [orgSession, setOrgSession] = useState(0);

  if (!isStaff) return null;

  const switchTo = async (type: 'org' | 'network', id: number) => {
    try {
      const result = await switchContext.mutateAsync({ type, id });
      updateCurrentUser(result);
      // useSwitchContext.onSuccess already routed to the new context's default
      // home (issue #4094). For the create flow we want /manage instead, so the
      // switcher's onCreated runs last and wins.
      onCreated?.();
    } catch {
      // Error surfaced by the mutation; leave the user where they are.
    }
  };

  return (
    <>
      <DropdownMenuRoot>
        <DropdownMenuTrigger asChild>
          <ActionButton
            action={Actions.create}
            mode="labeled"
            label="Create"
            endIcon={<Icons.expandMore style={{ fontSize: 18 }} />}
            testIdOverride={testIds.staffManagement.createMenuTrigger}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            data-testid={testIds.staffManagement.createOrgMenuItem}
            onClick={() => { setOrgSession((s) => s + 1); setCreateOrgOpen(true); }}
          >
            Create Organization
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={testIds.staffManagement.createNetworkMenuItem}
            onClick={() => { setNetworkSession((s) => s + 1); setCreateNetworkOpen(true); }}
          >
            Create Network
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuRoot>

      <CreateOrganizationDialog
        key={`org-${orgSession}`}
        open={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
        onSuccess={(organization) => { void switchTo('org', organization.id); }}
      />
      <CreateNetworkDialog
        key={`network-${networkSession}`}
        open={createNetworkOpen}
        onClose={() => setCreateNetworkOpen(false)}
        onSuccess={(network) => { void switchTo('network', network.id); }}
      />
    </>
  );
}
